// Copyright (c) 2022 by Move Closer

import { ComponentInternalInstance, toRefs } from '@vue/composition-api'

import {
  canHaveThemeProp,
  useVariantClass,
  UseVariantClassProvides,
  hasVariantProp,
  hasShapeProp,
  useShapeClass,
  UseShapeClassProvides,
  useSizeClass,
  UseSizeClassProvides,
  hasSizeProp,
  useThemeClass,
  UseThemeClassProvides
} from '../../composables'
import { ComponentObjectPropsOptions } from '../../vue-api'

import {
  BadgeProps,
  badgeShapeClassRegistry,
  badgeSizeClassRegistry,
  badgeThemeClassRegistry,
  badgeVariantClassRegistry
} from './Badge.contracts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const badgeProps: ComponentObjectPropsOptions<BadgeProps> = {
  /**
   * @see BadgeProps.label
   */
  label: {
    type: String,
    required: false
  },

  /**
   * @see BadgeProps.shape
   */
  ...hasShapeProp,

  /**
   * @see BadgeProps.size
   */
  ...hasSizeProp,

  /**
   * @see BadgeProps.theme
   */
  ...canHaveThemeProp,

  /**
   * @see BadgeProps.variant
   */
  ...hasVariantProp
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export type UseBadgeProvides = UseThemeClassProvides & UseSizeClassProvides & UseShapeClassProvides & UseVariantClassProvides

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const useBadge = (props: BadgeProps, internalInstance: ComponentInternalInstance | null): UseBadgeProvides => {
  if (internalInstance === null) {
    throw new Error('No component instance is found!')
  }

  const { type } = internalInstance
  const { shape, size, theme, variant } = toRefs(props)

  const themeClass = useThemeClass<string>(theme, badgeThemeClassRegistry, `${type._componentTag}`).themeClass ?? ''
  const shapeClass = useShapeClass<string>(shape, badgeShapeClassRegistry, `${type._componentTag}`).shapeClass ?? ''
  const sizeClass = useSizeClass<string>(size, badgeSizeClassRegistry, `${type._componentTag}`).sizeClass ?? ''
  const variantClass = useVariantClass<string>(variant, badgeVariantClassRegistry, `${type._componentTag}`).variantClass ?? ''

  return { themeClass, shapeClass, sizeClass, variantClass }
}
